import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardBody, CardHeader, Button, Textarea } from '../components/ui';
import { LinkifiedText } from '../components/LinkifiedText';
import { addEventReply, getEvent, subscribeEventReplies } from '../lib/firestore';
import { trackEvent } from '../lib/analytics';
import { formatExactDateTime, formatRelativeDateTime } from '../lib/dates';
import { eventPrimaryUrl, eventTypeLabel, formatEventRange } from '../lib/eventUtils';
import { memberLabel, memberPublicProfileUrl, useMemberDirectory } from '../lib/memberDirectory';
import { formatPersonName } from '../lib/names';
import type { Event, EventReply } from '../lib/types';
import { useAuth } from '../auth/AuthProvider';

function groupThread(replies: EventReply[]) {
  const top: EventReply[] = [];
  const children: Record<string, EventReply[]> = {};
  for (const r of replies) {
    if (!r.parentReplyId) top.push(r);
    else {
      children[r.parentReplyId] = children[r.parentReplyId] ?? [];
      children[r.parentReplyId].push(r);
    }
  }
  return { top, children };
}

export default function EventPage() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const memberDirectory = useMemberDirectory();
  const [event, setEvent] = React.useState<Event | null>(null);
  const [replies, setReplies] = React.useState<EventReply[]>([]);
  const [body, setBody] = React.useState('');
  const [replyTo, setReplyTo] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const trackedEventViewRef = React.useRef<string>('');

  React.useEffect(() => {
    if (!eventId) return;
    getEvent(eventId).then(setEvent).catch(() => setEvent(null));
    const unsub = subscribeEventReplies(eventId, setReplies);
    return () => unsub();
  }, [eventId]);

  React.useEffect(() => {
    if (!event?.id) return;
    if (trackedEventViewRef.current === event.id) return;
    trackedEventViewRef.current = event.id;
    void trackEvent('view_event', {
      event_id: event.id,
      event_type: event.type,
      has_action_url: Boolean(eventPrimaryUrl(event))
    });
  }, [event]);

  async function submit() {
    if (!eventId) return;
    if (!body.trim() || !user?.sub) return;
    if (submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      const trimmedBody = body.trim();
      const payload: any = {
        eventId,
        body: trimmedBody,
        authorProfileId: user.sub,
        authorDisplayName: formatPersonName(user.displayName)
      };

      if (replyTo) payload.parentReplyId = replyTo;

      await addEventReply(payload);
      await trackEvent('post_event_reply', {
        event_id: eventId,
        reply_target: replyTo ? 'reply' : 'event',
        body_length: trimmedBody.length
      });

      setBody('');
      setReplyTo(null);
    } catch (e: any) {
      console.error(e);
      setError(e?.message ? String(e.message) : 'Failed to post reply');
    } finally {
      setSubmitting(false);
    }
  }

  const { top, children } = groupThread(replies);
  const actionUrl = event ? eventPrimaryUrl(event) : '';

  if (!eventId) {
    return <div className="text-sm text-zinc-600">Missing event id</div>;
  }

  return (
    <div className="grid grid-cols-1 gap-4">
      <Card>
        <CardHeader
          title={event ? event.title : 'Event'}
          right={
            <div className="flex items-center gap-2">
              {actionUrl ? (
                <a
                  href={actionUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-900 transition hover:bg-zinc-50"
                >
                  Register
                </a>
              ) : null}
              <Link to="/events">
                <Button variant="secondary">Back</Button>
              </Link>
            </div>
          }
        />
        <CardBody className="space-y-4">
          {event ? (
            <div className="rounded-2xl border border-zinc-200 bg-white p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-zinc-300 bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700">
                  {eventTypeLabel(event.type)}
                </span>
                {event.source === 'wild-apricot' ? (
                  <span className="rounded-full border border-zinc-300 bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700">
                    Wild Apricot
                  </span>
                ) : null}
              </div>
              <div className="mt-3 whitespace-pre-wrap text-sm text-zinc-800">
                <LinkifiedText text={event.description} />
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-600">
                <span>{formatEventRange(event.startAt, event.endAt)}</span>
                {event.location ? <span>{event.location}</span> : null}
                {event.syncedAt ? <span>Updated from Wild Apricot {formatRelativeDateTime(event.syncedAt)}</span> : null}
              </div>
              {actionUrl ? (
                <a
                  className="mt-4 inline-flex rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-900 hover:bg-zinc-50"
                  href={actionUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Register
                </a>
              ) : null}
            </div>
          ) : (
            <div className="text-sm text-zinc-600">Loading</div>
          )}

          <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="text-xs font-semibold text-zinc-900">
                Reply {replyTo ? '(replying to a message)' : ''}
              </div>
              <div className="flex items-center gap-2">
                {replyTo ? (
                  <Button variant="ghost" onClick={() => setReplyTo(null)}>
                    Cancel reply-to
                  </Button>
                ) : null}
                <Button onClick={submit} disabled={!body.trim() || submitting}>
                  {submitting ? 'Posting...' : 'Post reply'}
                </Button>
              </div>
            </div>
            <Textarea
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write a reply"
            />
            {error ? (
              <div className="mt-2 rounded-xl border border-red-200 bg-red-50 p-2 text-xs text-red-800">
                {error}
              </div>
            ) : null}
          </div>

          <div className="space-y-3">
            {top.map((r) => (
              (() => {
                const author = memberLabel(r.authorProfileId, r.authorDisplayName, memberDirectory);
                return (
              <div key={r.id} className="rounded-2xl border border-zinc-200 bg-white p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1 whitespace-pre-wrap text-sm text-zinc-800">
                    <LinkifiedText text={r.body} />
                  </div>
                  <Button variant="ghost" onClick={() => setReplyTo(r.id)}>
                    Reply
                  </Button>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <a
                    href={memberPublicProfileUrl(r.authorProfileId)}
                    className="min-w-0 truncate text-xs text-zinc-700 underline decoration-zinc-300 underline-offset-2 hover:text-zinc-900"
                    title={author}
                  >
                    {author}
                  </a>
                  <span className="text-xs text-zinc-600" title={formatExactDateTime(r.updatedAt || r.createdAt)}>
                    Updated {formatRelativeDateTime(r.updatedAt || r.createdAt)}
                  </span>
                </div>

                {children[r.id]?.length ? (
                  <div className="mt-3 space-y-2 border-l border-zinc-200 pl-3">
                    {children[r.id].map((c) => (
                      (() => {
                        const childAuthor = memberLabel(c.authorProfileId, c.authorDisplayName, memberDirectory);
                        return (
                      <div key={c.id} className="rounded-xl border border-zinc-200 bg-zinc-50 p-3">
                        <div className="whitespace-pre-wrap text-sm text-zinc-800">
                          <LinkifiedText text={c.body} />
                        </div>
                        <div className="mt-2 flex items-center justify-between">
                          <a
                            href={memberPublicProfileUrl(c.authorProfileId)}
                            className="min-w-0 truncate text-xs text-zinc-700 underline decoration-zinc-300 underline-offset-2 hover:text-zinc-900"
                            title={childAuthor}
                          >
                            {childAuthor}
                          </a>
                          <span className="text-xs text-zinc-600" title={formatExactDateTime(c.updatedAt || c.createdAt)}>
                            Updated {formatRelativeDateTime(c.updatedAt || c.createdAt)}
                          </span>
                        </div>
                      </div>
                        );
                      })()
                    ))}
                  </div>
                ) : null}
              </div>
                );
              })()
            ))}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
